'use client';
import { memo, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import MUIIconButton from '@mui/material/IconButton';
import MUIButton from '@core/design-system/button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FiDownload } from '@react-icons/all-files/fi/FiDownload';
import { FiStar } from '@react-icons/all-files/fi/FiStar';
import { useTheme } from '@mui/material';

import { SocialSharePopover } from '../social-share-popover';
import { UiFundChips } from '@mais-retorno-front/libs/ui-fund-chips';
import { AiOutlineLineChart } from '@react-icons/all-files/ai/AiOutlineLineChart';
import { FiLogOut } from '@react-icons/all-files/fi/FiLogOut';
import { UiSheetFundHeader } from '@mais-retorno-front/libs/ui-sheet-fund-header';
import { DEFAULT_ROUTES } from '@mais-retorno-front/constants/routes';
import { ComparisonButton } from '@mais-retorno-front/modules/sheet-fund/comparison-button';
import { HighlightsText } from './highlights-text';
import { FaWhatsapp } from '@react-icons/all-files/fa/FaWhatsapp';

export const SponsoredFundHeader = memo<SponsoredFundHeaderProps>(
  ({
    nicename,
    favoriteFund,
    managerSite,
    handlerDownloadPDF,
    handleCreateSimulation,
    fundHeader,
    ctaContact,
    loadingStats,
  }) => {
    const router = useRouter();
    const theme = useTheme();

    return (
      <>
        <HighlightsText label="Lâmina Patrocinada" />

        <Stack
          gap={2}
          sx={(theme) => ({
            p: 3,
            py: 6,
            borderRadius: 3,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
          })}
        >
          <Stack
            flexDirection="row"
            gap={2}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Typography variant="h1" fontWeight={700} lineHeight={1.1}>
              {nicename}
            </Typography>

            <Stack flexDirection="row" gap={1}>
              <IconButton
                onClick={favoriteFund}
                aria-label="Favoritar fundo"
                title="Favoritar fundo"
              >
                <FiStar />
              </IconButton>

              <IconButton
                onClick={handlerDownloadPDF}
                aria-label="Baixar PDF"
                title="Baixar PDF"
              >
                <FiDownload />
              </IconButton>

              <SocialSharePopover
                tooltipText="Compartilhar Fundo"
                url={`https://maisretorno.com${router.asPath}`}
                description="Dá uma olhada nesse fundo"
                style={{
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  margin: 0,
                }}
              />
            </Stack>
          </Stack>

          <UiFundChips fundHeader={fundHeader} mainColor="secondary" />

          <Stack flexDirection="row" gap={1} flexWrap="wrap">
            <ComparisonButton
              fundCnpj={fundHeader.cnpj}
              benchmark={fundHeader.index}
            />

            <Button
              onClick={handleCreateSimulation}
              title="Crie uma simulação de carteira com esse fundo"
              endIcon={
                <AiOutlineLineChart color={theme.palette.secondary.main} />
              }
            >
              Simular
            </Button>
            {ctaContact && (
              <Button
                id="cta-link"
                target="_blank"
                rel="nofollow"
                component="a"
                title="Fale com a Gestora"
                href={ctaContact}
                endIcon={<FaWhatsapp color={theme.palette.secondary.main} />}
              >
                Falar com a gestora
              </Button>
            )}

            {managerSite && (
              <Button
                target="_blank"
                rel="nofollow"
                component="a"
                href={managerSite}
                title="Acesse o site da gestora do fundo"
                endIcon={<FiLogOut color={theme.palette.secondary.main} />}
              >
                Site da gestora
              </Button>
            )}
          </Stack>

          <UiSheetFundHeader
            fundHeader={fundHeader}
            fundStats={null}
            loadingStats={loadingStats}
            useLink
            routesLink={DEFAULT_ROUTES}
            Link={Link}
            headerStyle={{
              backgroundColor: '#ffffff26',
              '*': { color: '#fff' },
            }}
          />
        </Stack>
      </>
    );
  }
);

const Button = (props: any) => (
  <MUIButton
    color="secondary"
    variant="outlined"
    {...props}
    sx={{
      textTransform: 'none',
      color: '#fff',
      width: { xs: '100%', sm: 'auto' },
    }}
  >
    {props.children}
  </MUIButton>
);

const IconButton = ({ children, ...props }) => (
  <MUIIconButton
    color="primary"
    sx={{
      borderRadius: 2,
      backgroundColor: '#fff',
      '&: hover': { backgroundColor: '#e5e5e5' },
    }}
    {...props}
  >
    {children}
  </MUIIconButton>
);

interface SponsoredFundHeaderProps {
  nicename: string;
  ctaContact: string | null;
  favoriteFund: () => void;
  handlerDownloadPDF: () => void;
  handleCreateSimulation: () => void;
  fundHeader: any;
  managerSite: string;
  fundStats: any;
  loadingStats: boolean;
}
